import { useEffect, useMemo, useRef, useState } from 'react';

const CATEGORY_META = {
  'DATA SCIENCE': {
    accent: 'var(--cyan)',
    blurb: 'Upload, profile, clean, and visualize structured and unstructured datasets.',
  },
  'MACHINE LEARNING': {
    accent: 'var(--green)',
    blurb: 'Build intuition for model selection, training behavior, and evaluation tradeoffs.',
  },
  'DEEP LEARNING': {
    accent: 'var(--violet)',
    blurb: 'Explore architecture design, activation dynamics, and training internals.',
  },
  'COMPUTER VISION': {
    accent: 'var(--blue)',
    blurb: 'Work through image processing, classical CV, and neural perception workflows.',
  },
  'NATURAL LANGUAGE PROCESSING': {
    accent: 'var(--orange)',
    blurb: 'Move from tokenization and statistics to embeddings and transformer reasoning.',
  },
  'GENERATIVE AI': {
    accent: 'var(--red)',
    blurb: 'Experiment with latent spaces, generative models, prompting, and evaluation.',
  },
  'AGENTIC AI': {
    accent: 'var(--gold)',
    blurb: 'Study tool use, planning, memory, retrieval, and multi-agent coordination.',
  },
  'REINFORCEMENT LEARNING': {
    accent: 'var(--green)',
    blurb: 'Inspect reward dynamics, policy learning, exploration, and environment design.',
  },
  'QUANTUM AI': {
    accent: 'var(--violet)',
    blurb: 'Bridge quantum computing fundamentals with optimization and hybrid AI systems.',
  },
  'EDGE AI': {
    accent: 'var(--cyan)',
    blurb: 'Run browser-native inference stacks and model pipelines directly on device.',
  },
};

const alpha = (color, opacity) => `color-mix(in srgb, ${color} ${opacity}%, transparent)`;

function matchesQuery(lab, query) {
  if (!query) return true;
  const text = [lab.label, lab.sub, lab.cat, lab.id].join(' ').toLowerCase();
  return text.includes(query);
}

export default function LabsHub({ labCategories, onOpenLab, focusedCategory }) {
  const [query, setQuery] = useState('');
  const hubRef = useRef(null);
  const sectionRefs = useRef({});

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return labCategories
      .map((group) => ({
        ...group,
        labs: group.labs.filter((lab) => matchesQuery(lab, q)),
      }))
      .filter((group) => group.labs.length > 0);
  }, [labCategories, query]);

  const [activeCategory, setActiveCategory] = useState(
    focusedCategory || labCategories[0]?.category || ''
  );

  const scrollToCategory = (category, behavior = 'smooth') => {
    const node = sectionRefs.current[category];
    if (!node) return;
    node.scrollIntoView({ behavior, block: 'start' });
  };

  const handleCategorySelect = (category) => {
    setActiveCategory(category);
    requestAnimationFrame(() => {
      scrollToCategory(category, 'smooth');
    });
  };

  useEffect(() => {
    if (focusedCategory && labCategories.some((group) => group.category === focusedCategory)) {
      setActiveCategory(focusedCategory);
      requestAnimationFrame(() => {
        scrollToCategory(focusedCategory, 'smooth');
      });
    }
  }, [focusedCategory, labCategories]);

  useEffect(() => {
    if (!filteredCategories.some((group) => group.category === activeCategory)) {
      setActiveCategory(filteredCategories[0]?.category || '');
    }
  }, [filteredCategories, activeCategory]);

  const totalLabs = labCategories.reduce((sum, group) => sum + group.labs.length, 0);
  const activeMeta = CATEGORY_META[activeCategory] || {};

  return (
    <div
      ref={hubRef}
      style={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'grid',
        alignContent: 'start',
        gap: '14px',
        paddingRight: '2px',
      }}
    >
      <div
        style={{
          border: '1px solid var(--b1)',
          borderRadius: '6px',
          padding: '18px',
          background: 'linear-gradient(145deg, var(--surface), var(--deep))',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: '720px' }}>
            {/* <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.16em', marginBottom: '8px' }}>LAB DIRECTORY</div> */}
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '30px', lineHeight: 1.05, marginBottom: '10px', color: 'var(--t1)' }}>
              Interactive Labs
            </div>
            <div style={{ fontSize: '12px', color: 'var(--t2)', lineHeight: 1.75 }}>
A hands-on playground for data science, machine learning, deep learning, NLP, computer vision, agentic systems, quantum AI, and edge AI — no coding required. Fully interactive and immersive.
            </div>
          </div>
          <div style={{ minWidth: '220px', display: 'grid', gap: '8px', flex: '1 1 240px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px' }}>
              <div style={{ border: '1px solid var(--b1)', borderRadius: '4px', padding: '10px', background: 'var(--b0)' }}>
                <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '4px' }}>MODULES</div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--cyan)' }}>{labCategories.length}</div>
              </div>
              <div style={{ border: '1px solid var(--b1)', borderRadius: '4px', padding: '10px', background: 'var(--b0)' }}>
                <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '4px' }}>LABS</div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--green)' }}>{totalLabs}</div>
              </div>
              <div style={{ border: '1px solid var(--b1)', borderRadius: '4px', padding: '10px', background: 'var(--b0)' }}>
                <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '4px' }}>ACCESS</div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: activeMeta.accent || 'var(--violet)', lineHeight: 1.95 }}>
                  FREE-TIER
                </div>
              </div>
              <div style={{ border: '1px solid var(--b1)', borderRadius: '4px', padding: '10px', background: 'var(--b0)' }}>
                <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '4px' }}>RUNTIME</div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--gold)', lineHeight: 1.95 }}>
                  100% BROWSER
                </div>
                
              </div>
            </div>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search labs by name, category, or topic"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '4px',
                border: '1px solid var(--b1)',
                background: 'var(--deep)',
                color: 'var(--t1)',
                fontSize: '11px',
                fontFamily: 'JetBrains Mono',
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          padding: '2px 0',
        }}
      >
        {labCategories.map((group) => {
          const meta = CATEGORY_META[group.category] || {};
          const isActive = group.category === activeCategory;
          return (
            <button
              key={group.category}
              onClick={() => handleCategorySelect(group.category)}
              style={{
                padding: '9px 12px',
                borderRadius: '999px',
                border: `1px solid ${isActive ? meta.accent || 'var(--cyan)' : 'var(--b1)'}`,
                background: isActive ? alpha(meta.accent || 'var(--cyan)', 18) : 'transparent',
                color: isActive ? meta.accent || 'var(--cyan)' : 'var(--t2)',
                cursor: 'pointer',
                fontSize: '10px',
                fontFamily: 'JetBrains Mono',
                letterSpacing: '.08em',
                fontWeight: 700,
              }}
            >
              {group.category} [{group.labs.length}]
            </button>
          );
        })}
      </div>

      {filteredCategories.length === 0 ? (
        <div
          style={{
            border: '1px dashed var(--b1)',
            borderRadius: '6px',
            padding: '26px',
            textAlign: 'center',
            background: 'var(--deep)',
            color: 'var(--t2)',
          }}
        >
          No labs match this search.
        </div>
      ) : (
        filteredCategories.map((group) => {
          const meta = CATEGORY_META[group.category] || {};
          return (
            <section
              key={group.category}
              ref={(node) => {
                sectionRefs.current[group.category] = node;
              }}
              style={{
                scrollMarginTop: '14px',
                border: `1px solid ${alpha(meta.accent || 'var(--b1)', 33)}`,
                borderRadius: '6px',
                padding: '16px',
                background: 'linear-gradient(160deg, var(--surface), var(--deep))',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '9px', color: meta.accent || 'var(--t3)', letterSpacing: '.16em', marginBottom: '6px' }}>
                    MODULE FAMILY
                  </div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '24px', color: 'var(--t1)', lineHeight: 1.1, marginBottom: '8px' }}>
                    {group.category}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--t2)', lineHeight: 1.7, maxWidth: '760px' }}>
                    {meta.blurb || 'Open the labs in this module family directly from here.'}
                  </div>
                </div>
                <div
                  style={{
                    minWidth: '92px',
                    border: `1px solid ${alpha(meta.accent || 'var(--b1)', 33)}`,
                    borderRadius: '4px',
                    padding: '10px 12px',
                    background: alpha(meta.accent || 'var(--cyan)', 12),
                  }}
                >
                  <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.12em', marginBottom: '4px' }}>LAB COUNT</div>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: meta.accent || 'var(--cyan)' }}>{group.labs.length}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                {group.labs.map((lab) => (
                  <button
                    key={lab.id}
                    onClick={() => onOpenLab?.(lab)}
                    style={{
                      textAlign: 'left',
                      border: `1px solid ${alpha(lab.color, 44)}`,
                      borderRadius: '5px',
                      background: `linear-gradient(145deg, ${alpha(lab.color, 14)}, var(--b0))`,
                      padding: '14px 14px 12px',
                      cursor: 'pointer',
                      minHeight: '148px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                      <div
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: `1px solid ${alpha(lab.color, 55)}`,
                          background: alpha(lab.color, 18),
                          color: lab.color,
                          fontSize: '18px',
                          flexShrink: 0,
                        }}
                      >
                        {lab.icon}
                      </div>
                      <div style={{ fontSize: '8px', color: 'var(--t3)', letterSpacing: '.14em', whiteSpace: 'nowrap' }}>
                        OPEN LAB
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--t1)', fontWeight: 700, lineHeight: 1.35, marginBottom: '8px' }}>
                      {lab.label}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--t2)', lineHeight: 1.65, marginBottom: '10px' }}>
                      {lab.sub}
                    </div>
                    <div style={{ fontSize: '9px', color: lab.color, fontFamily: 'JetBrains Mono', letterSpacing: '.08em' }}>
                      {lab.id.toUpperCase()}
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
